import type { IChatMessage } from '@/apiClient/types/chat'

/**
 * Messages fetched one at a time via GET `/chats/{id}/messages/{messageId}`, keyed by
 * message id, per chat. They are kept beside `chat.messages` rather than spliced into
 * it: that array is a contiguous window from the newest message, and both the chat
 * view and the `loadOlderMessages` offset depend on it staying contiguous — inserting
 * a far older message would render a stray message and skip one while scrolling back.
 */
export type FetchedMessagesByChatId = Record<
  string,
  Record<string, IChatMessage>
>

/**
 * The message wherever it currently lives: the loaded window wins over the
 * individually fetched copy, since it also carries in-flight stream updates.
 */
export function findChatMessage(
  windowMessages: IChatMessage[] | undefined,
  fetchedMessages: Record<string, IChatMessage> | undefined,
  messageId: string,
): IChatMessage | undefined {
  if (!messageId) return undefined
  const loaded = windowMessages?.find((message) => message.id === messageId)
  return loaded ?? fetchedMessages?.[messageId]
}

/**
 * Every message the store holds for a chat, oldest first: individually fetched
 * messages (ordered by timestamp) followed by the loaded window. A fetched message is
 * by construction outside that window, which always reaches the newest message, so it
 * is older than everything in it. Messages already in the window are dropped from the
 * fetched side — the window copy is the fresher one.
 */
export function chatMessagesWithFetched(
  windowMessages: IChatMessage[] | undefined,
  fetchedMessages: Record<string, IChatMessage> | undefined,
): IChatMessage[] {
  const loaded = windowMessages ?? []
  const fetched = Object.values(fetchedMessages ?? {})
  if (!fetched.length) return loaded

  const loadedIds = new Set(loaded.map((message) => message.id))
  const olderFirst = fetched
    .filter((message) => !loadedIds.has(message.id))
    .sort((a, b) => a.timestamp - b.timestamp)

  return olderFirst.length ? [...olderFirst, ...loaded] : loaded
}
